import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../api';
import { getActiveSelection, computePopoverPosition, replaceSelectionInPlace } from './selection';
import { logger } from '../../utils/logger';

/**
 * State machine + behaviors for the ⌘. AI rewrite popover.
 * Owns the global keyboard listener, the rewrite API call, and the
 * replace/copy result actions. The component stays presentational.
 */
export function useAIRewrite() {
  const [snapshot, setSnapshot] = useState(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [rewritten, setRewritten] = useState('');
  const [activeStyle, setActiveStyle] = useState(null);
  const [loading, setLoading] = useState(false);

  const close = useCallback(() => {
    setSnapshot(null);
    setRewritten('');
    setActiveStyle(null);
    setLoading(false);
  }, []);

  // Global keyboard listener — opens / closes the menu.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        const sel = getActiveSelection();
        if (!sel) {
          toast.message('Highlight some text first', { description: 'Then press ⌘. to invoke AI rewrite.' });
          return;
        }
        e.preventDefault();
        setPos(computePopoverPosition(sel));
        setSnapshot(sel);
        setRewritten('');
        setActiveStyle(null);
      }
      if (e.key === 'Escape' && (snapshot || rewritten)) {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [snapshot, rewritten, close]);

  const run = useCallback(async (styleKey) => {
    if (!snapshot) return;
    setLoading(true);
    setActiveStyle(styleKey);
    setRewritten('');
    try {
      const res = await api.post('/ai/rewrite', { text: snapshot.text, style: styleKey });
      setRewritten(res.data?.rewritten || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI rewrite failed');
      setActiveStyle(null);
    } finally {
      setLoading(false);
    }
  }, [snapshot]);

  const replaceInPlace = useCallback(() => {
    if (!snapshot || !rewritten) return;
    try {
      if (snapshot.kind === 'input') {
        if (replaceSelectionInPlace(snapshot, rewritten)) toast.success('Replaced');
      } else if (snapshot.kind === 'range') {
        // Range rewrite is "copy to clipboard" only — most app text is React-managed
        navigator.clipboard.writeText(rewritten).then(
          () => toast.success('Copied to clipboard'),
          () => toast.error('Could not copy'),
        );
      }
    } catch (err) {
      logger.error('Replace-in-place failed:', err);
      toast.error('Could not replace text');
    }
    close();
  }, [snapshot, rewritten, close]);

  const copyResult = useCallback(() => {
    if (!rewritten) return;
    navigator.clipboard.writeText(rewritten).then(
      () => toast.success('Copied'),
      () => toast.error('Could not copy'),
    );
    close();
  }, [rewritten, close]);

  return { snapshot, pos, rewritten, activeStyle, loading, close, run, replaceInPlace, copyResult };
}
